import { useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Mail, Check, Loader2, X } from "lucide-react";

const FREE_PROVIDERS = ["@gmail.com", "@yahoo.com", "@outlook.com", "@aol.com", "@icloud.com", "@hotmail.com"];

function providerLabel(email) {
  if (!email) return null;
  const e = email.toLowerCase();
  for (const p of FREE_PROVIDERS) {
    if (e.endsWith(p)) return p.replace("@", "").replace(".com", "");
  }
  return null;
}

/**
 * Suggests a small set of professional aliases to "migrate" to the user's
 * verified domain, pre-filled with their signup email as the forwarding target.
 */
export default function RestoreFromGmailCard({ user, domains, existingAliases, onCreated }) {
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set(["info", "contact"]));
  // Start collapsed when user already has ≥1 alias on the domain (they've already begun
  // setting things up — don't overwhelm them with a big migration card).
  const hasExistingAlias = (existingAliases || []).length > 0;
  const [expanded, setExpanded] = useState(!hasExistingAlias);

  const provider = providerLabel(user?.email);

  // Pick the first verified domain (or any domain if none verified)
  const targetDomain = useMemo(() => {
    if (!domains?.length) return null;
    return domains.find((d) => d.verified) || domains[0];
  }, [domains]);

  // Derive personal alias from the signup email local-part (e.g. matt@gmail.com → "matt")
  const personalAlias = useMemo(() => {
    if (!user?.email) return null;
    const local = user.email.split("@")[0].toLowerCase().replace(/[^a-z0-9]/g, "");
    return local && local.length <= 24 ? local : null;
  }, [user?.email]);

  // Suggestions: personal (firstname) + generic business aliases
  const suggestions = useMemo(() => {
    const base = ["info", "contact", "hello"];
    const list = [...base];
    if (personalAlias && !base.includes(personalAlias)) list.unshift(personalAlias);
    // Filter out any already in use on this domain
    const used = new Set(
      (existingAliases || [])
        .filter((a) => targetDomain && a.domain === targetDomain.domain)
        .map((a) => a.alias)
    );
    return list.filter((a) => !used.has(a));
  }, [personalAlias, existingAliases, targetDomain]);

  // Gate conditions: only show if free-inbox user, has a domain, and we have fresh suggestions
  if (dismissed) return null;
  if (!provider) return null;
  if (!targetDomain) return null;
  if (suggestions.length === 0) return null;

  // Collapsed (minimized) state — shown when the user already has at least one alias on the domain.
  if (!expanded) {
    return (
      <section className="mb-8" data-testid="restore-from-gmail-card-collapsed">
        <button
          onClick={() => setExpanded(true)}
          data-testid="restore-expand-btn"
          className="w-full flex items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-[#FFF8F6] to-white border border-[#F26953]/20 px-5 py-3.5 hover:border-[#F26953]/40 hover:shadow-sm transition-all"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-[#F26953] grid place-items-center flex-shrink-0">
              <Sparkles className="h-4 w-4 text-white"/>
            </div>
            <div className="text-sm text-[#0F172A] text-left min-w-0">
              <span className="font-semibold">Bring your {provider} over</span>
              <span className="text-[#475569] hidden sm:inline"> — migrate {suggestions.length} more address{suggestions.length !== 1 ? "es" : ""} to {targetDomain.domain}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-semibold text-[#F26953]">Show</span>
            <span
              role="button"
              aria-label="Dismiss"
              data-testid="restore-dismiss-collapsed"
              onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
              className="p-1 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-100"
            >
              <X className="h-4 w-4"/>
            </span>
          </div>
        </button>
      </section>
    );
  }

  // Auto-select first 2 suggestions by default (user can toggle)
  if (selected.size === 0 && suggestions.length > 0) {
    // no-op — defaults already set via initialState; if all defaults filtered, first available gets picked below
  }
  // Clean selected set to only valid current suggestions
  const validSelected = new Set(Array.from(selected).filter((s) => suggestions.includes(s)));
  const effectiveSelected = validSelected.size > 0
    ? validSelected
    : new Set(suggestions.slice(0, 2));

  const toggle = (a) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(a)) n.delete(a); else n.add(a);
      return n;
    });
  };

  const handleMigrate = async () => {
    if (effectiveSelected.size === 0) {
      toast.error("Pick at least one address to migrate");
      return;
    }
    setBusy(true);
    const created = [];
    const failed = [];
    for (const a of effectiveSelected) {
      try {
        await api.post("/aliases", {
          domain_id: targetDomain.id,
          alias: a,
          destinations: [user.email],
        });
        created.push(a);
      } catch (e) {
        failed.push({ a, err: formatApiError(e) });
      }
    }
    setBusy(false);
    if (created.length > 0) {
      toast.success(`Migrated ${created.length} address${created.length !== 1 ? "es" : ""} from ${provider}`);
      setDismissed(true);
      onCreated?.();
    }
    if (failed.length > 0) {
      toast.error(`Couldn't create: ${failed.map((f) => f.a).join(", ")} — ${failed[0].err}`);
    }
  };

  return (
    <section className="mb-8" data-testid="restore-from-gmail-card">
      <div className="relative rounded-3xl bg-gradient-to-br from-[#FFF8F6] via-white to-white border border-[#F26953]/20 overflow-hidden">
        <button
          onClick={() => setDismissed(true)}
          data-testid="restore-dismiss"
          aria-label="Dismiss"
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <X className="h-4 w-4"/>
        </button>

        <div className="p-8 md:p-10">
          <div className="flex items-start gap-4 mb-5">
            <div className="h-12 w-12 rounded-2xl bg-[#F26953] grid place-items-center flex-shrink-0 shadow-sm">
              <Sparkles className="h-6 w-6 text-white"/>
            </div>
            <div className="flex-1 min-w-0 pr-8">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26953] mb-1">
                Bring your {provider} over
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] tracking-tight">
                Migrate your {provider} identity to <span className="mono text-[#F26953]">{targetDomain.domain}</span>
              </h2>
              <p className="text-sm text-[#475569] mt-2">
                We noticed you signed up with <span className="mono text-[#0F172A] font-semibold">{user.email}</span>. Want us to auto-create professional addresses at your new domain? They'll all forward right back to your existing {provider} inbox — one click, zero setup.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {suggestions.map((a) => {
              const active = effectiveSelected.has(a);
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggle(a)}
                  data-testid={`restore-chip-${a}`}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 font-mono text-sm transition-all ${
                    active
                      ? "border-[#F26953] bg-[#FEE7E1] text-[#0F172A] font-semibold"
                      : "border-[#E2E8F0] bg-white text-[#475569] hover:border-slate-300"
                  }`}
                >
                  {active && <Check className="h-3.5 w-3.5 text-[#F26953]"/>}
                  {a}@{targetDomain.domain}
                </button>
              );
            })}
          </div>

          <div className="rounded-xl bg-white/60 border border-[#F26953]/15 p-4 text-left mb-5 text-xs text-[#475569] flex items-start gap-2">
            <Mail className="h-4 w-4 text-[#F26953] flex-shrink-0 mt-0.5"/>
            <span>
              All selected addresses will forward to <span className="mono text-[#0F172A] font-semibold">{user.email}</span>. Cancel or change any of them anytime.
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleMigrate}
              disabled={busy || effectiveSelected.size === 0}
              data-testid="restore-submit"
              className="inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60 shadow-sm"
            >
              {busy
                ? <><Loader2 className="h-4 w-4 animate-spin"/> Migrating…</>
                : <>Migrate {effectiveSelected.size} address{effectiveSelected.size !== 1 ? "es" : ""}</>}
            </button>
            <button
              onClick={() => setDismissed(true)}
              data-testid="restore-skip"
              className="text-sm text-[#475569] font-medium px-4 py-3 rounded-lg hover:bg-slate-100"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
